import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Award, RotateCw, ClipboardList } from 'lucide-react';
import { useTrainingLang } from '../i18n/TrainingLangContext';

const QuizResults = ({ result, questions, relatedSurvey, onRetry, onExit }) => {
  const navigate = useNavigate();
  const { t } = useTrainingLang();
  const passed = !!result?.passed;
  const reviewById = {};
  if (Array.isArray(result?.review)) {
    for (const r of result.review) reviewById[r.question_id] = r;
  }
  const showReview = Array.isArray(result?.review);

  return (
    <div className="max-w-3xl mx-auto p-6 text-slate-100">
      <div
        className={`rounded-lg p-6 mb-6 border ${
          passed
            ? 'bg-emerald-900/30 border-emerald-700'
            : 'bg-rose-900/30 border-rose-700'
        }`}
      >
        <div className="flex items-center gap-3 mb-2">
          {passed ? (
            <Award className="w-8 h-8 text-emerald-400" />
          ) : (
            <XCircle className="w-8 h-8 text-rose-400" />
          )}
          <h2 className="text-2xl font-bold">
            {passed ? t('resultPassed') : t('resultFailed')}
          </h2>
        </div>
        <p className="text-lg">
          {t('scoreLabel')}{' '}
          <span className="font-semibold">{Number(result.score_pct).toFixed(1)}%</span>
          <span className="text-slate-300">
            {' '}
            {t('correctOfTotal', {
              correct: result.correct_count,
              total: result.question_count,
            })}
          </span>
        </p>
      </div>

      {showReview && (
        <div className="space-y-3 mb-6">
          <h3 className="text-lg font-semibold">{t('reviewHeading')}</h3>
          {questions.map((q, i) => {
            const r = reviewById[q.id];
            if (!r) return null;
            const chosen = q.options.find((o) => o.id === r.chosen_option_id);
            const correct = q.options.find((o) => o.id === r.correct_option_id);
            return (
              <div
                key={q.id}
                className={`rounded-lg p-4 border ${
                  r.correct ? 'border-emerald-700 bg-emerald-900/10' : 'border-rose-700 bg-rose-900/10'
                }`}
              >
                <div className="flex items-start gap-2 mb-2">
                  {r.correct ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
                  ) : (
                    <XCircle className="w-5 h-5 text-rose-400 mt-0.5" />
                  )}
                  <div className="font-medium">{i + 1}. {q.question_text}</div>
                </div>
                <div className="text-sm pl-7 space-y-1">
                  <div className={r.correct ? 'text-emerald-300' : 'text-rose-300'}>
                    {t('yourAnswer')} {chosen?.option_text || <em>{t('noAnswer')}</em>}
                  </div>
                  {!r.correct && correct && (
                    <div className="text-emerald-300">
                      {t('correctAnswer')} {correct.option_text}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {relatedSurvey?.survey && (
        <div className="mb-6 rounded-lg p-4 border border-sky-700 bg-sky-900/20 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-slate-200">
            <ClipboardList className="w-5 h-5 text-sky-300" />
            <span>{t('surveyPrompt')}</span>
          </div>
          <button
            type="button"
            onClick={() => navigate(`/tiles/ksp_training/survey/${relatedSurvey.survey.id}`)}
            className="px-4 py-2 rounded bg-sky-600 hover:bg-sky-500 text-white flex items-center gap-2"
          >
            {t('takeSurvey')}
          </button>
        </div>
      )}

      <div className="flex gap-3">
        {!passed && (
          <button
            type="button"
            onClick={onRetry}
            className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white flex items-center gap-2"
          >
            <RotateCw className="w-4 h-4" /> {t('actionRetry')}
          </button>
        )}
        <button
          type="button"
          onClick={onExit}
          className="px-4 py-2 rounded bg-slate-700 hover:bg-slate-600 text-slate-100"
        >
          {t('backToDashboard')}
        </button>
      </div>
    </div>
  );
};

export default QuizResults;
